<template>
  <div id="header">

    <div id="left-group">

      <span id="title">{{ strings.Header.title }}</span>

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('assemble')"
          icon="bolt"
          :title="strings.Header.HeaderButton.assemble"
          @click="appAssemble()"
      />

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('reset')"
          icon="redo"
          :title="strings.Header.HeaderButton.reset"
          @click="appReset()"
      />

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('step') || !simulationCanRun()"
          icon="angle-right"
          :title="strings.Header.HeaderButton.step"
          @click="appStep()"
      />

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('continue') || !simulationCanRun()"
          icon="angle-double-right"
          :title="strings.Header.HeaderButton.continue"
          @click="appContinue()"
      />

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('load')"
          icon="file"
          :title="strings.Header.HeaderButton.load"
          @click="$refs.uploadFile.click()"
      />

      <HeaderButton
          :disabled="isButtonDisabledForCurrentTab('save')"
          icon="save"
          :title="strings.Header.HeaderButton.save"
          @click="$refs.SaveDialog.show()"
      />

      <input type="file"
             ref="uploadFile"
             style="display:none"
             @change="appLoad"
      >
    </div>

    <div id="right-group">

      <button @click="$refs.SettingsDialog.show()">
        <i class="fa fa-cog"></i>
      </button>

      <button @click="$refs.AboutDialog.show()">
        <i class="fa fa-info"></i>
      </button>

    </div>

    <SaveDialog
        ref="SaveDialog"
        :scopedStrings="strings.SaveDialog"
    />

    <SettingsDialog
        ref="SettingsDialog"
        :scopedStrings="strings.SettingsDialog"
        :settings="settings"
    />

    <AboutDialog
        ref="AboutDialog"
        :scopedStrings="strings.AboutDialog"
    />

  </div>
</template>

<script>
import AboutDialog from './AboutDialog.vue'
import HeaderButton from './HeaderButton.vue'
import SettingsDialog from './SettingsDialog.vue'
import SaveDialog from './SaveDialog.vue'

export default {
  props: {
    strings: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    },
    simulatorController : {
      type : Object,
      required: true
    },
    yasController : {
      type : Object,
      required: true
    },
    hclController : {
      type : Object,
      required: true
    }
  },
  components: {
    AboutDialog,
    HeaderButton,
    SettingsDialog,
    SaveDialog
  },
  methods: {
    appAssemble() {
      try {
        this.simulatorController.reset()

// get the hcl code and compile it
        let hclCode = this.$parent.getHclCode()
        if (hclCode !== undefined) {
          let hclCompilationResult = undefined
          try {
            hclCompilationResult = this.hclController.assemble(hclCode)
          } catch(error) { // The HCL injection failed.
            throw [this.strings.Header.messages.hclInjectionError,
              error.message]
          }
          if (hclCompilationResult !== undefined && hclCompilationResult.errors.length > 0) {
            throw [this.strings.Header.messages.hclAssembleError,
              hclCompilationResult.errors[0].message]
          }
        }

// get the source code from the editor and compile it
        var ys = this.$parent.getY86Code()
        const yasCompilationResult = this.yasController.assemble(ys) // assemble loads the program in the memory under the hood
        if (yasCompilationResult.errors.length > 0) {
          throw [this.strings.Header.messages.yasAssembleError,
            this.strings.Header.messages.line + yasCompilationResult.errors[0].line
            + this.strings.Header.messages.lineMessageSeparator + yasCompilationResult.errors[0].message]
        }

// get the object code and put it in the view
        this.$parent.initObjCode(yasCompilationResult, this.simulatorController.getCurrentKernelHighlightRules())

// get the new simulator state
        this.updateAll()

// Switch to the execution tab when all the code was successfully assembled
        this.$emit('updateCurrentTab', this.getTabId('execution'))
      } catch (error) {
        this.$emit('showPromptDialog', 'error', error[0], error[1])
      }
    },
    appReset() { // Resets the machine state
      this.simulatorController.reset()
      let compilationResult = this.$parent.getCompilationResult()
      if(compilationResult !== "")
        this.simulatorController.loadProgram(compilationResult.output)

      this.updateAll()
    },
    appStep() { // Makes a step and get the new simulator state
      try {
        this.simulatorController.step()
        this.updateAll()
      } catch (error) {
        this.$emit('showPromptDialog', 'error', error)
      }
    },
    appContinue() { // Makes all steps as possible
      try {
        this.simulatorController.continue()
        this.updateAll()
      } catch (error) {
        this.$emit('showPromptDialog', 'error', error)
      }
    },
    loadY86(jsonContent) {
      this.$parent.setY86Code(jsonContent)
    },

    loadOldY86Files(reader) { // Loads .ys files
      reader.onload = e => this.$parent.setY86Code(e.target.result)
      this.$emit('showPromptDialog', 'success', this.strings.LoadDialog.singleElementLoaded + this.strings.Tabs.editor + ".")
      this.$emit('updateCurrentTab', this.getTabId('editor'))
    },

    loadMachineState(jsonContent) {
      if(jsonContent.compilationResult.output === undefined)
         return
      this.$parent.initObjCode(jsonContent.compilationResult, this.simulatorController.getCurrentKernelHighlightRules())
      this.simulatorController.loadProgram(jsonContent.compilationResult.output)
      this.simulatorController.setCPUState(jsonContent.cpuState)
      this.simulatorController.setRegistersView(jsonContent.registers)
      this.simulatorController.setFlagsView(jsonContent.flags)
      this.simulatorController.setStatusView(jsonContent.status)
      this.simulatorController.setPerformanceView(jsonContent.performance)
      this.simulatorController.setMemoryView(jsonContent.memory)
      this.updateAll()
    },

    loadInstructionSet(jsonContent) {
      this.$parent.setInstructionSet(jsonContent)
    },

    loadHcl(jsonContent) {
      this.$parent.setHclCode(jsonContent)
    },

    loadOldHCLFiles(reader) { // Loads .hcl files
      reader.onload = e => this.$parent.setHclCode(e.target.result)
      this.$emit('showPromptDialog', 'success', this.strings.LoadDialog.singleElementLoaded + this.strings.Tabs.hcl + ".")
      this.$emit('updateCurrentTab', this.getTabId('hcl'))
    },

    async loadFileContent(jsonContent) { // Loads elements specified in the file
      let loadedElements = []
      let hasError = false

      if (jsonContent.hasOwnProperty('machineState')){
        try{
          if(jsonContent.machineState.kernel >= this.settings.kernels.length){
            throw new Error(`The kernel of index ${jsonContent.machineState.kernel} doesn't exist.`)
          }

          this.settings.current.kernel = parseInt(jsonContent.machineState.kernel, 10)  // will trigger the updateKernel routine
          await this.$parent.$nextTick()                                                // wait for the update completion
          this.loadMachineState(jsonContent.machineState)
          loadedElements.push(this.strings.Tabs.machineState)
        }
        catch (error) {
          hasError = true
          this.$emit('showPromptDialog', 'error', this.strings.LoadDialog.loadErrorKernel, error.message)
        }
      }
      if (jsonContent.hasOwnProperty('editor')) {
        this.loadY86(jsonContent.editor)
        loadedElements.splice(0, 0, this.strings.Tabs.editor)
      }
      if(jsonContent.hasOwnProperty('instructionSet')) {
        try {
          this.loadInstructionSet(jsonContent.instructionSet)
          loadedElements.push(this.strings.Tabs.instructionSet)
        } catch (error) {
          hasError = true
          this.$emit('showPromptDialog', 'error', this.strings.InstructionSet.messages.loadJsonError, error.message)
        }
      }
      if(jsonContent.hasOwnProperty('hcl')) {
        this.loadHcl(jsonContent.hcl)
        loadedElements.push(this.strings.Tabs.hcl)
      }

      if(!hasError) {
        let lastElement = loadedElements[loadedElements.length - 1]
        loadedElements = loadedElements.join(", ")
        if(loadedElements.split(", ").length >= 2){ // there's at least two elements, the last two words need to be separated with an "and"
          loadedElements = loadedElements.replace(", " + lastElement, this.strings.LoadDialog.and + lastElement)
          this.$emit('showPromptDialog', 'success', this.strings.LoadDialog.multipleElementsLoaded + loadedElements + ".")
        }
        else{
          this.$emit('showPromptDialog', 'success', this.strings.LoadDialog.singleElementLoaded + loadedElements + ".")
        }
      }
    },

    appLoad(event) {
      let file = event.target.files[0]
      let filename = file.name.split('-')[0]
      let filenameExtension = file.name.split('.')[1]
      const reader = new FileReader()

      if(filenameExtension === "ys") // For backward compatibility reasons
        this.loadOldY86Files(reader)
      else if(filenameExtension === "hcl") // For backward compatibility reasons
        this.loadOldHCLFiles(reader)
      else{
        reader.onload = e => {
          let jsonContent = null
          try{
            jsonContent = JSON.parse(e.target.result)
          } catch (error){
            this.$emit('showPromptDialog', 'error', this.strings.LoadDialog.loadErrorFile, error)
            return
          }
          this.loadFileContent(jsonContent)
        }

        if(filename === "machineState")
          this.$emit('updateCurrentTab', this.getTabId('execution'))
        else if(filename === "instructionSet")
          this.$emit('updateCurrentTab', this.getTabId('instructionSet'))
      }

      reader.readAsText(file)
      event.target.value = '' // reset uploadFile input to force some browsers to trigger the onChange event again even if the user selected the same file twice
    },

    saveY86InJson(index, itemsLength) {
      let data = ""
      data += "\"editor\" : " + JSON.stringify(this.$parent.getY86Code())
      data += index !== itemsLength-1 ?  ",\n" : "\n"
      return data
    },

    saveMachineState(index, itemsLength) {
      let data = ""
      data += "\"machineState\" : {\n"
      data += "\"kernel\" : \"" + this.settings.current.kernel + "\",\n"
      data += "\"compilationResult\" : " + JSON.stringify(this.$parent.getCompilationResult()) + ",\n"
      data += "\"cpuState\" : " + JSON.stringify(this.simulatorController.getCPUState()) + ",\n"
      data += "\"registers\" : " + JSON.stringify(this.simulatorController.getRegistersView()) + ",\n"
      data += "\"flags\" : " + JSON.stringify(this.simulatorController.getFlagsView()) + ",\n"
      data += "\"status\" : " + JSON.stringify(this.simulatorController.getStatusView()) + ",\n"
      data += "\"performance\" : " + JSON.stringify(this.simulatorController.getPerformanceView()) + ",\n"
      data += "\"memory\" : " + JSON.stringify(this.simulatorController.getChangedMemoryView()  ) + "\n}"
      data += index !== itemsLength-1 ?  ",\n" : "\n"
      return data
    },

    saveInstructionSet(index, itemsLength) {
      let data = ""
      data += "\"instructionSet\" : " + JSON.stringify(this.$parent.getInstructionSet(), null, 2)
      data += index !== itemsLength-1 ? ",\n" : "\n"
      return data
    },

    saveHclInJson(index, itemsLength) {
      let data = ""
      data += "\"hcl\" : " + JSON.stringify(this.$parent.getHclCode())
      data += index !== itemsLength-1 ? ",\n" : "\n"
      return data
    },

    addTimestamp() { // Gives a timestamp with the format --year-month-day--hours-minutes
      let now = new Date()
      let dayString = "--" + now.getFullYear() + "-" + ("0" + (now.getMonth() + 1)).slice(-2) + "-" + ("0" + now.getDate()).slice(-2)
      let hourString = "--" + ("0" + now.getHours()).slice(-2) + "-" + ("0" + now.getMinutes()).slice(-2)
      return dayString + hourString
    },

    saveNow(data, filename) { // Saves the data specified in the parameter data in a file named by the parameter filename
      let blob = new Blob([data], { type: 'text/plain;charset=utf-8;' })

      if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, filename)
      } else {
        let link = document.createElement('a')
        if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          let url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', filename)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    },

    appSave(saveItems) { // Saves all tabs specified by the parameter saveItems
      if (saveItems.length === 0)
        return

      let filename = ""
      let filenameExtension = ""
      let data = ""

      if(saveItems.length === 1 && saveItems[0] === "editor" ) { // For backward compatibility reasons
        filename = "y86Code"
        filenameExtension = ".ys"
        data = this.$parent.getY86Code()
      }
      else if (saveItems.length === 1 && saveItems[0] === "hcl" ) { // For backward compatibility reasons
        filename = "hclCode"
        filenameExtension = ".hcl"
        data = this.$parent.getHclCode()
      }
      else{
        filenameExtension = '.json'
        data += "{\n"

        for(let index = 0; index < saveItems.length; ++index) {
          switch (saveItems[index]) {
            case "editor":
              data += this.saveY86InJson(index,saveItems.length)
              break
            case "execution":
              filename = "machineState"
              data += this.saveMachineState(index,saveItems.length)
              break
            case "instructionSet" :
              filename = "instructionSet"
              data += this.saveInstructionSet(index,saveItems.length)
              break
            case "hcl":
              data += this.saveHclInJson(index,saveItems.length)
              break
            default:
              return
          }
        }
        data += "}"

        if(saveItems.length > 1)
          filename = "y86simulator"
      }

      filename += this.addTimestamp() + filenameExtension
      this.saveNow(data, filename)
    },
    getTabId (tabName) { // Gives the identifier of the tab specified by the parameter tabName
      let tabs = this.settings.tabs,
          match = this.settings.current.tab
      for (let i = 0; i < tabs.length; i++) {
        if (tabName.localeCompare(tabs[i].name) === 0) {
          match = tabs[i].id
        }
      }
      return match
    },
    getCurrentTab() { // Gives the name of the current tab
      return this.settings.tabs[this.settings.current.tab]
    },
    isButtonDisabledForCurrentTab(buttonRef) {
      return !this.getCurrentTab().buttons.includes(buttonRef)
    },
    simulationCanRun() {
      return this.$parent.simulationCanRun()
    },
    updateAll() { // update all views of the simulator state
      this.$parent.updateAll()
    }
  }
}
</script>

<style>
#header {
  display: flex;
  padding: 6px;
  background-color: var(--Header-background);
  color: var(--Header-foreground);
}

#header > div > * {
  margin: 0 4px;
}

#left-group {
  flex: 1;
}

#right-group > button {
  width: 36px;
}

#left-group,
#right-group {
  display: flex;
  align-items: center;
}

#header #title,
#header a {
  margin-bottom: -6px;
}

#header #title {
  padding: 0 10px;
  font-size: 24px;
}

#header > div > button {
  padding: 6px 10px;
  background-color: var(--HeaderButton-enabled-background);
  color: var(--HeaderButton-enabled-foreground);
  font-size: 16px;
  transition-duration: .3s;
  opacity: 1;
}

#header > div > button:hover {
  background-color: var(--HeaderButton-hover-background);
}

#header > div > button:disabled {
  cursor: not-allowed;
  background-color: var(--HeaderButton-disabled-background);
  color: var(--HeaderButton-disabled-foreground);
}

#header > div > button > i {
  font-size: 16px;
}

#header > #left-group > button > i {
  padding-right: 8px;
}

#header > div > a > img {
  height: 30px;
  width: 30px;
}

@media only screen and (max-width: 1080px) {
  #header > div > button {
    display: inline-flex;
    align-items: flex-end;
    font-size: 10px;
    text-transform: uppercase;
  }

  #header > #right-group > button {
    display: block;
  }

  #header > div > button > i {
    font-size: 13px;
  }
}
</style>
